import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Twitter, Instagram, Send, Sparkles, Mail, Phone, MapPin } from 'lucide-react';
import { siteData } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Linkedin,
  Twitter,
  Instagram,
};

const Footer = () => {
  const { company, footer } = siteData;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <footer id="contact" className="bg-[var(--text-primary)] text-white">
      {/* Contact Section */}
      <div className="container py-20 md:py-32">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-[var(--accent-primary)] text-sm font-medium mb-4">
                Get In Touch
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Transform Your Business?
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Book a free strategy call and discover how AI-first solutions can revolutionise your operations.
              </p>

              <div className="space-y-4 mb-8">
                <a
                  href={`mailto:${company.email}`}
                  className="flex items-center gap-3 text-white/80 hover:text-[var(--accent-primary)] transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span>{company.email}</span>
                </a>
                <a
                  href={`tel:${company.phone}`}
                  className="flex items-center gap-3 text-white/80 hover:text-[var(--accent-primary)] transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  <span>{company.phone}</span>
                </a>
                <div className="flex items-center gap-3 text-white/80">
                  <MapPin className="w-5 h-5" />
                  <span>Remote-First, UK Based</span>
                </div>
              </div>

              {/* Social Links - LinkedIn only (with placeholder until real URL provided) */}
              <div className="flex items-center gap-4">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[var(--accent-primary)] hover:text-[var(--text-primary)] transition-all duration-200"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-white/10"
            >
              {/* Direct access note */}
              <div className="mb-6 pb-6 border-b border-white/10">
                <p className="text-[var(--accent-primary)] font-semibold text-sm mb-1">
                  Direct access. No sales teams.
                </p>
                <p className="text-white/70 text-sm">
                  You will be speaking directly with the Directors.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-white/90 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[var(--accent-primary)] focus:ring-2 focus:ring-[var(--accent-primary)]/20 transition-all"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white/90 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[var(--accent-primary)] focus:ring-2 focus:ring-[var(--accent-primary)]/20 transition-all"
                    placeholder="john@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-white/90 mb-2">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[var(--accent-primary)] focus:ring-2 focus:ring-[var(--accent-primary)]/20 transition-all resize-none"
                    placeholder="Tell us about your project..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Logo */}
            <a href="#" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[var(--accent-primary)] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[var(--text-primary)]" />
              </div>
              <span className="font-semibold">{company.name}</span>
            </a>

            {/* Quick Links */}
            <div className="flex items-center gap-6">
              {footer.quickLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.url}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <a
                href="/privacy"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                Privacy
              </a>
              <a
                href="/terms"
                className="text-sm text-white/60 hover:text-white transition-colors"
              >
                Terms
              </a>
            </div>

            {/* Copyright */}
            <p className="text-sm text-white/40">
              {footer.copyright}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
